
import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { mockProviders, mockCategories } from '@/data/mockData';
import { useBooking } from '@/contexts/BookingContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { useStudios, Studio } from '@/hooks/use-studios';
import StudioMap from './StudioMap';

const ProviderStep = () => {
  const {
    selectedProvider,
    setSelectedProvider,
    setCurrentStep
  } = useBooking();
  const {
    toast
  } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch studios from Supabase
  const {
    studios,
    loading: studiosLoading,
    error
  } = useStudios(selectedCategory, searchQuery);

  useEffect(() => {
    // Check URL parameters for category or search
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
    if (searchParam) {
      setSearchQuery(searchParam);
    }

    // Simulate loading state
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchParams]);

  useEffect(() => {
    // Display error message if studios could not be loaded
    if (error) {
      toast({
        title: "Fehler beim Laden der Studios",
        description: "Es werden Beispiel-Studios angezeigt. Bitte versuche es später erneut.",
        variant: "destructive",
        duration: 5000
      });
    }
  }, [error, toast]);

  // Merge mock providers with real studios data (during transition period)
  const filteredProviders = mockProviders.filter(provider => {
    const matchesSearch = searchQuery === '' || provider.name.toLowerCase().includes(searchQuery.toLowerCase()) || provider.beschreibung.toLowerCase().includes(searchQuery.toLowerCase()) || provider.adresse.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === null || provider.branche === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Handle studio selection from the map
  const handleStudioSelect = (studio: Studio) => {
    // Find corresponding provider in mockProviders or adapt studio to provider format
    const matchingProvider = mockProviders.find(p => p.name === studio.name) || {
      id: studio.id,
      name: studio.name,
      beschreibung: studio.description || '',
      branche: 'etc',
      // Default category
      adresse: 'Basierend auf Kartenposition',
      standort: {
        latitude: studio.geo_location?.latitude || 0,
        longitude: studio.geo_location?.longitude || 0
      },
      imageUrl: "https://via.placeholder.com/300x200"
    };

    // Select the provider
    handleSelectProvider(matchingProvider);
  };

  // Verbesserte Funktion für die Anbieterauswahl
  const handleSelectProvider = (provider: any) => {
    console.log("Provider selected:", provider.name);
    setSelectedProvider(provider);

    // Direkter Aufruf von setCurrentStep mit minimaler Verzögerung
    setTimeout(() => {
      console.log("Moving to step 2 with provider:", provider.name);
      setCurrentStep(2);
      toast({
        title: "Anbieter ausgewählt",
        description: `Du hast ${provider.name} ausgewählt. Wähle nun Service, Personal & Termin.`,
        duration: 3000
      });
    }, 50);
  };

  // Animation variants for staggered children
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={containerVariants} className="animate-fade-in">
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-chrona-gray-400" size={18} />
          <Input placeholder="Suche nach Namen, Adresse oder Beschreibung..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-12 bg-chrona-gray-800/40 backdrop-blur border-white/10 focus:border-chrona-accent/50 focus:ring-chrona-accent/30 transition-all rounded-xl px-[88px]" />
        </div>
        
        <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
          <motion.button variants={itemVariants} className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap ${selectedCategory === null ? 'bg-chrona-accent text-white shadow-md shadow-chrona-accent/20' : 'bg-chrona-gray-800/60 hover:bg-chrona-gray-700/60 text-chrona-text border border-white/10'}`} onClick={() => setSelectedCategory(null)}>
            Alle
          </motion.button>
          
          {mockCategories.map(category => <motion.button key={category.id} variants={itemVariants} className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap ${selectedCategory === category.id ? 'bg-chrona-accent text-white shadow-md shadow-chrona-accent/20' : 'bg-chrona-gray-800/60 hover:bg-chrona-gray-700/60 text-chrona-text border border-white/10'}`} onClick={() => setSelectedCategory(category.id)}>
              {category.name}
            </motion.button>)}
        </div>
      </div>
      
      {/* Map Section */}
      {!isLoading && !studiosLoading ? <StudioMap studios={studios} selectedStudio={null} onStudioSelect={handleStudioSelect} /> : <div className="w-full h-[400px] relative mb-6 rounded-xl overflow-hidden bg-chrona-gray-800/40">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-chrona-accent"></div>
              <div className="absolute inset-0 border-t-2 border-chrona-accent opacity-20 rounded-full"></div>
            </div>
          </div>
        </div>}
      
      {/* Provider grid */}
      {isLoading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(item => <Card key={item} className="overflow-hidden glass-card">
              <Skeleton className="h-40 w-full bg-chrona-gray-800/60" />
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <Skeleton className="h-5 w-20 bg-chrona-gray-800/60 rounded-full" />
                  <Skeleton className="h-5 w-10 bg-chrona-gray-800/60 rounded-full" />
                </div>
                <Skeleton className="h-6 w-3/4 bg-chrona-gray-800/60 rounded-full mb-2" />
                <Skeleton className="h-4 w-full bg-chrona-gray-800/60 rounded-full mb-1" />
                <Skeleton className="h-4 w-2/3 bg-chrona-gray-800/60 rounded-full mb-4" />
                <div className="flex items-center justify-between pt-2 border-t border-chrona-gray-800/40">
                  <Skeleton className="h-8 w-20 bg-chrona-gray-800/60 rounded-full" />
                  <Skeleton className="h-8 w-20 bg-chrona-gray-800/60 rounded-full" />
                </div>
              </CardContent>
            </Card>)}
        </div> : <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" variants={containerVariants}>
          {filteredProviders.map(provider => <motion.div key={provider.id} variants={itemVariants}>
              <Card className={`cursor-pointer overflow-hidden transition-all duration-300 card-3d border border-white/10 bg-black/40 backdrop-blur-xl ${selectedProvider?.id === provider.id ? 'ring-2 ring-chrona-accent shadow-xl shadow-chrona-accent/10' : 'shadow-lg hover:shadow-xl hover:transform hover:scale-[1.02]'}`} onClick={() => setSelectedProvider(provider)}>
                <div className="h-40 overflow-hidden">
                  <img src={provider.imageUrl || "https://via.placeholder.com/300x200"} alt={provider.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="inline-block px-3 py-1 bg-chrona-gray-800/60 backdrop-blur text-chrona-accent text-xs rounded-full mb-1 border border-white/10">
                      {mockCategories.find(c => c.id === provider.branche)?.name || "Service"}
                    </div>
                    <div className="flex items-center bg-chrona-gray-800/60 backdrop-blur px-2 py-1 rounded-full border border-white/10">
                      <Star size={12} className="text-chrona-accent mr-1" fill="currentColor" />
                      <span className="text-xs">4.9</span>
                    </div>
                  </div>
                  <h3 className="font-medium text-lg text-chrona-text">{provider.name}</h3>
                  <div className="flex items-center text-sm text-chrona-gray-400 mt-1">
                    <MapPin size={14} className="flex-shrink-0 mr-1" />
                    <span className="truncate">{provider.adresse}</span>
                  </div>
                  <p className="text-sm text-chrona-gray-300 line-clamp-2 mt-2">{provider.beschreibung}</p>
                  
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-chrona-gray-800/40">
                    <Button variant="outline" size="sm" className="text-xs border-chrona-accent/50 text-chrona-accent hover:bg-chrona-accent hover:text-white transition-colors" onClick={e => {
                e.stopPropagation();
                navigate(`/provider/${provider.id}`);
              }}>
                      Details
                    </Button>
                    <Button size="sm" className="text-xs bg-chrona-accent hover:bg-chrona-accent-light active:scale-95 transition-all" onClick={e => {
                e.stopPropagation();
                handleSelectProvider(provider);
              }}>
                      Auswählen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
          
          {filteredProviders.length === 0 && <motion.div className="col-span-full text-center py-12 glass-card rounded-2xl border border-white/10 backdrop-blur-xl bg-black/40" variants={itemVariants}>
              <p className="text-chrona-text text-lg mb-2">Keine Anbieter gefunden.</p>
              {searchQuery && <p className="text-sm text-chrona-gray-400 mb-6">
                  Versuche es mit einem anderen Suchbegriff oder wähle eine andere Kategorie.
                </p>}
              <Button onClick={() => {
          setSearchQuery('');
          setSelectedCategory(null);
        }} variant="outline" className="border-white/10 hover:bg-white/10 hover:text-chrona-accent transition-colors">
                Filter zurücksetzen
              </Button>
            </motion.div>}
        </motion.div>}
    </motion.div>
  );
};

export default ProviderStep;
